import Router from 'next/router';
import { useCallback } from 'react';

import { usePublicUrlShowcaseBoardHref } from '~/components/v2/components/showcase/hooks/usePublicUrlShowcaseBoardHref';
import { useSeedCentralizedPublicBoard } from '~/hooks/useSeedCentralizedPublicBoard';
import { type PublicBoardContextValue } from '~/providers/PublicBoardProvider';
import { type PublicShowcaseLayoutActivePage } from '~/providers/PublicShowcaseProvider';

export type UsePublicUrlBoardNavigationProps = {
  /** The share link whose data this surface renders — the board seed's cache key. */
  shortId: string;
  /** The surface's route base — every board route is `${basePath}/b/{boardId}`. */
  basePath: string;
  /**
   * The page the viewer is on, composed into drill routes as the `?page={slug}` trail. Pass null
   * on surfaces with no sub-pages (the standalone board pages) — drills then carry no trail.
   */
  activePage: PublicShowcaseLayoutActivePage;
};

/**
 * The URL surfaces' board-drill action: push the board route (carrying the listing page's slug as
 * the `?page={slug}` breadcrumb trail), then seed the centralized board so the drilled board's
 * title renders without waiting for its fetch. One definition for every surface whose URL really
 * works — the live `/a/…` board page, the `/a/…` collection page, and the editor's Preview iframe.
 * Requires the redux + react-query providers ABOVE it (see useSeedCentralizedPublicBoard).
 */
export const usePublicUrlBoardNavigation = ({ shortId, basePath, activePage }: UsePublicUrlBoardNavigationProps) => {
  const { seedCentralizedPublicBoard } = useSeedCentralizedPublicBoard();

  // The same href builder the mounts supply to PublicShowcaseLayout's contract — the drill
  // pushes below reuse it so the board href scheme has exactly one definition.
  const { getBoardNavigationHref } = usePublicUrlShowcaseBoardHref({ basePath });

  const onNavigateToBoard = useCallback<PublicBoardContextValue['onNavigateToBoard']>(
    async ({ boardId, boardTitle, page }) => {
      const currentPageSlug = activePage === null ? null : activePage.pageSlug;
      const pageSlug = page !== undefined ? page.pageSlug : currentPageSlug;

      await Router.push(getBoardNavigationHref({ boardId, pageSlug }), undefined, { shallow: true });

      if (boardTitle !== undefined) {
        seedCentralizedPublicBoard({ shortId, boardId, boardTitle });
      }
    },
    [activePage, getBoardNavigationHref, seedCentralizedPublicBoard, shortId],
  );

  return { onNavigateToBoard };
};
